import { EXPERIENCE_IDS } from '@/components/admin/experiences/contracts';
import { PersonaCardGauge, PersonaCardIdentity } from '@/components/admin/personas/persona-card';
import { VersionPill } from '@/components/admin/versioned/version-pill';
import type { InvolvementLevel, PersonaCard } from '@matter_extractor/shared';
import { Button, TextField } from '@pms-digital/design-system';
import { useState } from 'react';

/**
 * Le sélecteur de persona de §10.2 : la recherche par nom et les cartes de la
 * bibliothèque en variante avatar et jauge (maquette `OngletPersonaVide`).
 *
 * §10.2 says what it shows, and the mockup says how: « la recherche par nom et
 * les cartes de la bibliothèque en variante avatar et jauge, trois par rangée,
 * chacune avec ses pastilles et un bouton "Choisir" ; les retirés n'y sont
 * pas ».
 *
 * ## The two card blocks are the PERSONA's, imported and not copied
 *
 * `PersonaCardIdentity` and `PersonaCardGauge` are what `/admin/personas`
 * draws, and a persona has to look the same wherever it is read: the avatar,
 * the subtitle and the mini-gauge are how §15 makes « lequel parle comment »
 * legible at a glance, and a second spelling here would be a second persona.
 * What is NOT reused is `VersionedCardTile` itself: a card of this selector is
 * a CHOICE and not a link, so it carries a button where the library's carries a
 * chevron - and a tile that wrapped its whole body in an anchor cannot hold one.
 *
 * ## A new choice is `latest`, and the version is chosen AFTER (vitrine.md V16)
 *
 * The selector used to carry a « Tag joué » select. A reference now starts at
 * `latest` whatever is chosen, and the pill-menu of the chosen persona
 * (`ReferenceControl`, on its card) is where a precise version is picked.
 *
 * ## The retired ones are absent, and the search is local
 *
 * §4 takes a retired persona out of every selector, so they never reach this
 * list. The search filters the cards already in memory, like every filter of a
 * library (`versioned-filters.ts`): the whole list arrives in one read, so a
 * keystroke costs a `filter()`.
 */

export interface ExperiencePersonaPickerProps {
  /** The library, retired ones included - this component drops them (§4). */
  personas: readonly PersonaCard[];
  /** The current involvement scale, which the gauge of each card draws against. */
  levels: readonly InvolvementLevel[];
  onChoose: (personaId: string) => void;
  onClose: () => void;
}

export function ExperiencePersonaPicker({
  personas,
  levels,
  onChoose,
  onClose,
}: ExperiencePersonaPickerProps) {
  const [search, setSearch] = useState('');

  const needle = search.trim().toLowerCase();
  const shown = personas.filter(
    (card) => card.retiredAt === null && card.name.toLowerCase().includes(needle),
  );

  return (
    <div
      data-testid={EXPERIENCE_IDS.personaPicker}
      className="space-y-4 rounded-md border border-gray-100 bg-gray-50 p-4"
    >
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div className="w-64 max-w-full">
          <TextField
            label="Chercher un persona"
            data-testid={EXPERIENCE_IDS.personaSearch}
            type="search"
            value={search}
            placeholder="Rechercher"
            onChange={(event) => setSearch(event.currentTarget.value)}
          />
        </div>
      </div>

      {shown.length === 0 ? (
        <p className="text-gray-700 text-small">
          Aucun persona ne répond à cette recherche. La bibliothèque se remplit dans Personas.
        </p>
      ) : (
        <ul className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {shown.map((card) => (
            <li
              key={card.id}
              data-testid={EXPERIENCE_IDS.personaOption}
              className="flex flex-col gap-3 rounded-md border-2 border-gray-200 bg-white p-4"
            >
              <PersonaCardIdentity card={card} />
              <PersonaCardGauge card={card} levels={levels} />
              <div className="flex flex-wrap gap-2">
                {/* ABSENTE, jamais un « v- » : rien n'a été passé en production (§4). */}
                {card.production === null ? null : (
                  <VersionPill entity="persona" tag="production" version={card.production.number} />
                )}
                <VersionPill entity="persona" tag="development" version={card.development.number} />
              </div>
              <div className="mt-auto self-start">
                <Button type="button" variant="accent" size="sm" onClick={() => onChoose(card.id)}>
                  Choisir
                </Button>
              </div>
            </li>
          ))}
        </ul>
      )}

      <Button type="button" variant="ghost" size="sm" onClick={onClose}>
        Fermer le sélecteur
      </Button>
    </div>
  );
}
